import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/Button.jsx';
import Card from '../components/Card.jsx';
import NotFound from './NotFound.jsx';
import {
  getCategoryPageContent,
  getInvitationCategory,
  getTemplatesByCategory,
  getVisibleInvitationCategories,
  getCategoryExtras,
} from '../lib/invitationCatalog.js';
import '../styles/Invitations.css';
import '../styles/InvitationCategory.css';

export default function InvitationCategory({ categoryKey }) {
  const category = getInvitationCategory(categoryKey);
  const page = getCategoryPageContent(categoryKey);
  const designs = getTemplatesByCategory(categoryKey);
  const extras = getCategoryExtras(categoryKey);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  if (!category || !page) {
    return <NotFound />;
  }

  const relatedCategories = getVisibleInvitationCategories().filter(
    (item) => item.value !== categoryKey
  );

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div className="invitation-category-page">
      <section className="invitation-category-hero">
        <div className="hero-decoration"></div>
        <div className="hero-content-wrapper">
          <p className="invitation-category-eyebrow">{page.eyebrow}</p>
          <h1 className="hero-title-animated">{page.heading}</h1>
          <p className="invitation-category-description">{page.description}</p>

          <div className="invitation-category-actions">
            <Link to="/book">
              <Button variant="primary" size="large">
                {page.ctaLabel}
              </Button>
            </Link>
            <Link to="/invitations" className="invitation-category-secondary-link">
              View all digital invitation designs →
            </Link>
          </div>

          <div className="invitation-category-metrics">
            {page.metrics.map((item, index) => (
              <article key={item.label} className="invitation-category-metric-card" style={{ animationDelay: `${index * 0.1}s` }}>
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="invitation-category-benefits">
        <div className="invitation-section-heading">
          <h2>Why this invitation category works</h2>
          <p>{page.lead}</p>
        </div>
        <div className="invitation-category-highlights">
          {page.highlights.map((highlight) => (
            <article key={highlight} className="invitation-category-highlight-card">
              <p>{highlight}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="invitation-category-templates-section">
        <div className="invitation-section-heading">
          <h2>{category.label} invitation designs</h2>
          <p>{page.designLead}</p>
        </div>

        <div className="templates-grid invitation-category-grid">
          {designs.map((design) => (
            <div key={design.id} className="template-card-wrapper">
              <a href={design.previewPath} className="preview-overlay" target="_blank" rel="noopener noreferrer">
                <span className="preview-text">Click to Preview</span>
              </a>
              <Card
                title={design.title}
                description={design.description}
                image={design.image}
                className="template-card"
                category={design.category}
                categoryLabel={design.categoryLabel || category.label}
              />
            </div>
          ))}
        </div>
      </section>

      {extras?.testimonials && extras.testimonials.length > 0 && (
        <section className="invitation-category-testimonials">
          <div className="invitation-section-heading">
            <h2>What our clients say</h2>
            <p>Real feedback from couples who trusted us with their special day</p>
          </div>
          <div className="testimonials-grid">
            {extras.testimonials.map((testimonial, index) => (
              <article key={index} className="testimonial-card">
                <div className="testimonial-rating">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="star">⭐</span>
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <strong>{testimonial.name}</strong>
                  <span>{testimonial.event}</span>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {extras?.faqs && extras.faqs.length > 0 && (
        <section className="invitation-category-faq">
          <div className="invitation-section-heading">
            <h2>Frequently asked questions</h2>
            <p>Everything you need to know about {category.label.toLowerCase()} digital invitations</p>
          </div>
          <div className="faq-list">
            {extras.faqs.map((faq, index) => (
              <div key={index} className={`faq-item ${openFaqIndex === index ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleFaq(index)}>
                  <span>{faq.question}</span>
                  <span className="faq-icon">{openFaqIndex === index ? '−' : '+'}</span>
                </button>
                {openFaqIndex === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="invitation-category-related">
        <div className="invitation-section-heading">
          <h2>Explore more digital invitation categories</h2>
          <p>
            Compare other event-specific landing pages if you are still choosing the right invitation style.
          </p>
        </div>

        <div className="invitation-category-links">
          {relatedCategories.map((item) => (
            <Link key={item.value} to={item.path} className="invitation-category-link-card">
              <span className="invitation-category-link-label">{item.label}</span>
              <h3>{item.headline}</h3>
              <p>{item.blurb}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="invitation-category-cta-bottom">
        <div className="cta-bottom-content">
          <h2>Ready to create your {category.label.toLowerCase()} invitation?</h2>
          <p>Get started in minutes with our easy booking process</p>
          <Link to="/book">
            <Button variant="primary" size="large">
              Book Your Invitation Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
